import { ChangeDetectionStrategy, Component, OnDestroy, OnInit } from '@angular/core';
import { FormBuilder, FormGroup } from '@angular/forms';
import { PageEvent } from '@angular/material/paginator';
import { ActivatedRoute, Router } from '@angular/router';
import { TdDialogService } from '@covalent/core/dialogs';
import { TdLoadingService } from '@covalent/core/loading';
import { BehaviorSubject, combineLatest, Observable, Subject } from 'rxjs';
import {
  debounceTime,
  distinctUntilChanged,
  map,
  share,
  switchMap,
  take,
  takeUntil,
  tap,
} from 'rxjs/operators';
import { PilotDrugAlcoholRecordEditDialogComponent } from 'src/app/pilots/components/pilot-drug-alcohol-record-edit-dialog/pilot-drug-alcohol-record-edit-dialog.component';
import { PilotEmploymentRecordAssignmentToDutyEditDialogComponent } from 'src/app/pilots/components/pilot-employment-record-assignment-to-duty-edit-dialog/pilot-employment-record-assignment-to-duty-edit-dialog.component';
import { PilotEmploymentRecordEditDialogComponent } from 'src/app/pilots/components/pilot-employment-record-edit-dialog/pilot-employment-record-edit-dialog.component';
import { PilotFinalDisciplinaryActionEditDialogComponent } from 'src/app/pilots/components/pilot-final-disciplinary-action-edit-dialog/pilot-final-disciplinary-action-edit-dialog.component';
import { PilotNdrRecordEditDialogComponent } from 'src/app/pilots/components/pilot-ndr-record-edit-dialog/pilot-ndr-record-edit-dialog.component';
import { PilotTrainingRecordEditDialogComponent } from 'src/app/pilots/components/pilot-training-record-edit-dialog/pilot-training-record-edit-dialog.component';
import { AssignmentToDutyHttpClient } from 'src/app/shared/flight-log-api/assignment-to-duties/assignment-to-duties-client.service';
import { DrugAlcoholRecordHttpClient } from 'src/app/shared/flight-log-api/drug-alcohol-records/drug-alcohol-records-client.service';
import { EmploymentRecordHttpClient } from 'src/app/shared/flight-log-api/employment-records/employment-records-client.service';
import { FinalDisciplinaryActionHttpClient } from 'src/app/shared/flight-log-api/final-disciplinary-actions/final-disciplinary-actions-client.service';
import { EntityType } from 'src/app/shared/flight-log-api/flight-logs/flight-log-client.model';
import { NdrRecordHttpClient } from 'src/app/shared/flight-log-api/ndr-records/ndr-records-client.service';
import { TrainingRecordHttpClient } from 'src/app/shared/flight-log-api/training-records/training-records-client.service';
import { TransmissionRunBatchItemSummaryHttpClient } from 'src/app/shared/flight-log-api/transmission-run-batch-item-summaries/transmission-run-batch-item-summaries-client.service';
import { TransmissionRunHttpClient } from 'src/app/shared/flight-log-api/transmission-runs/transmission-runs-client.service';
import { TransmissionRunEntity } from 'src/store/transmission-run/transmission-run.model';
import { sleep } from 'src/util/async';
import {
  getPageIndex,
  getPageQueryParams,
  getPageSize,
  tryGetSkipTakeOrDefault,
} from 'src/util/ui/pagination';
import { AirCarrierViewModel } from 'src/view-models/air-carrier-view-model';
import { TransmissionRunBatchItemSummaryViewModel } from 'src/view-models/transmission-run-batch-item-summary-view-model';
import { TransmissionRunViewModel } from 'src/view-models/transmission-run-view-model';

interface RouteParams {
  searchTerm?: string;
  page?: string;
  pageSize?: string;
}

interface TransmissionRunDetailParams {
  showErrorsOnly?: boolean;
  skip?: number;
  take?: number;
  searchTerm?: string;
}

export function humanizeEntityName(entityName: string) {
  switch (entityName) {
    case EntityType.PRDPilotRecord:
      return 'Pilot Record';
    case EntityType.PRDEmploymentRecord:
      return 'Employment Record';
    case EntityType.PRDAssignmentToDutyRecord:
      return 'Assignment To Duty Record';
    case EntityType.PRDTrainingRecord:
      return 'Training Record';
    case EntityType.PRDFinalDisciplinaryAction:
      return 'Final Disciplinary Action';
    case EntityType.PRDNDRRecord:
      return 'NDR Record';
    case EntityType.PRDDrugAndAlcoholRecord:
      return 'Drug And Alcohol Record';
  }
}

@Component({
  selector: 'fl-transmission-run-detail-list-page',
  templateUrl: './transmission-run-detail-list-page.component.html',
  styleUrls: ['./transmission-run-detail-list-page.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class TransmissionRunDetailListPageComponent implements OnInit, OnDestroy {
  public transmissionRunBatchItemSummaries$: Observable<TransmissionRunBatchItemSummaryViewModel[]>;
  public transmissionRuns$: Observable<TransmissionRunViewModel[]>;
  public transmissionRun$: Observable<TransmissionRunEntity | undefined>;
  public transmissionRunId: string | undefined;
  public airCarriers$: Observable<AirCarrierViewModel[]>;
  public total$: Observable<number>;

  public displayedColumns = [
    'dateRun',
    'pilotName',
    'entityType',
    'isProcessed',
    'prdProcessedDate',
    'hasError',
    'errorDescription',
    'prdTrackingNumber',
  ];

  public page$ = new Subject<PageEvent>();
  public defaultPageSize = 10;
  public pageSizeOptions = [this.defaultPageSize, 20, 40, 80];
  public pageIndex$: Observable<number>;
  public pageSize$: Observable<number | undefined>;
  public isValidDateRange$: Observable<boolean>;
  public airCarrierAbbreviation: string;

  public maxNoOfDays = 30;

  public _pageRefresh$ = new BehaviorSubject<{}>({});
  public _onDestroy$ = new Subject();

  public loadingKey = 'transmission-runs-batch-item-summary-view';

  public transmissionRunBatchItemSummaryParameters: FormGroup;

  constructor(
    private _assignmentToDuty: AssignmentToDutyHttpClient,
    private _drugAlcoholRecord: DrugAlcoholRecordHttpClient,
    private _employmentRecord: EmploymentRecordHttpClient,
    private _finalDisciplinaryAction: FinalDisciplinaryActionHttpClient,
    private _ndrRecord: NdrRecordHttpClient,
    private _trainingRecord: TrainingRecordHttpClient,
    private _transmissionRun: TransmissionRunHttpClient,
    private _transmissionRunBatchItemSummary: TransmissionRunBatchItemSummaryHttpClient,
    private _route: ActivatedRoute,
    private _router: Router,
    private _loading: TdLoadingService,
    private _dialogService: TdDialogService,
    private _fb: FormBuilder,
  ) {
    const transmissionRunResult$ = this._route.params.pipe(
      map((t) => t.id),
      switchMap((id) => this._transmissionRun.getById(id).pipe(map((r) => r))),
    );
    this.transmissionRun$ = transmissionRunResult$.pipe(map((res) => res.results[0]));

    const transmissionRunBatchItemSummariesResults$ = combineLatest([
      this.transmissionRun$,
      this._route.queryParams,
      this._pageRefresh$,
    ]).pipe(
      switchMap(([transmissionRun, params]) => {
        const detailParams = this.parseRouteParams(params, this.defaultPageSize);
        this.markAsLoading();
        this.transmissionRunId = transmissionRun?.id;
        return this._transmissionRunBatchItemSummary.getAll({
          transmissionRunId: transmissionRun?.id,
          hasError: this.hasError(detailParams.showErrorsOnly),
          skip: detailParams.skip,
          take: detailParams.take,
          searchTerm: detailParams.searchTerm,
        });
      }),
      distinctUntilChanged(),
      tap(() => this.markAsLoaded()),
      share(),
    );

    this.transmissionRunBatchItemSummaries$ = transmissionRunBatchItemSummariesResults$.pipe(
      map((r) => r.results),
    );
    this.total$ = transmissionRunBatchItemSummariesResults$.pipe(
      map((r) => {
        return r.totalCount;
      }),
    );
  }

  public ngOnInit() {
    this.transmissionRunBatchItemSummaryParameters = this._fb.group({
      showErrorsOnly: false,
      searchTerm: undefined,
    });

    this.transmissionRunBatchItemSummaryParameters.valueChanges
      .pipe(debounceTime(500), takeUntil(this._onDestroy$))
      .subscribe((value: TransmissionRunDetailParams) => {
        const airCarrierAbbreviation = this._route.snapshot.data.airCarrier.abbreviation;
        const params = {
          hasError: this.hasError(value.showErrorsOnly),
          searchTerm: value.searchTerm ?? undefined,
        };
        this._router.navigate(
          [`/prd/${airCarrierAbbreviation}/transmission-runs`, this.transmissionRunId],
          {
            queryParams: params,
          },
        );
      });

    this.pageIndex$ = getPageIndex(this._route);
    this.pageSize$ = getPageSize(this._route, this.defaultPageSize);
    this.page$.pipe(takeUntil(this._onDestroy$)).subscribe((page) => {
      this.patchQueryParams(getPageQueryParams(page, this.defaultPageSize));
    });

    this.airCarrierAbbreviation = this._route.snapshot.data.airCarrier.abbreviation;
  }

  public onPage(page: PageEvent) {
    this.page$.next(page);
  }

  public ngOnDestroy() {
    this._onDestroy$.next();
  }

  public markAsLoading() {
    this._loading.register(this.loadingKey);
  }

  public markAsLoaded() {
    this._loading.resolveAll(this.loadingKey);
  }

  private parseRouteParams(
    queryParams: RouteParams,
    defaultPageSize: number,
  ): TransmissionRunDetailParams {
    return {
      showErrorsOnly: this.showErrorsOnly?.value,
      searchTerm: queryParams.searchTerm,
      ...tryGetSkipTakeOrDefault(queryParams, defaultPageSize),
    };
  }

  public async openEditDialog(entityName: string, entityId: string, pilotId: string) {
    let data = {};
    let component;
    const airCarrierAbbreviation = this._route.snapshot.data.airCarrier.abbreviation;
    switch (entityName) {
      case EntityType.PRDPilotRecord:
        const url = this._router.serializeUrl(
          this._router.createUrlTree([`/prd/${airCarrierAbbreviation}/pilots/${pilotId}`]),
        );
        window.open(url, '_blank');
        break;
      case EntityType.PRDEmploymentRecord:
        component = PilotEmploymentRecordEditDialogComponent;
        const employmentRecord = await this._employmentRecord
          .getById(entityId)
          .pipe(map((col) => col.results[0]))
          .toPromise();
        data = {
          pilotId,
          employmentRecord,
        };
        break;
      case EntityType.PRDAssignmentToDutyRecord:
        component = PilotEmploymentRecordAssignmentToDutyEditDialogComponent;
        const assignmentToDuty = await this._assignmentToDuty
          .getById(entityId)
          .pipe(map((col) => col.results[0]))
          .toPromise();
        data = {
          employmentRecordId: assignmentToDuty.employmentRecordId,
          assignmentToDuty,
        };
        break;
      case EntityType.PRDTrainingRecord:
        component = PilotTrainingRecordEditDialogComponent;
        const trainingRecord = await this._trainingRecord
          .getById(entityId)
          .pipe(map((col) => col.results[0]))
          .toPromise();
        data = {
          pilotId,
          trainingRecord,
        };
        break;
      case EntityType.PRDFinalDisciplinaryAction:
        component = PilotFinalDisciplinaryActionEditDialogComponent;
        const finalDisciplinaryAction = await this._finalDisciplinaryAction
          .getById(entityId)
          .pipe(map((col) => col.results[0]))
          .toPromise();
        data = {
          pilotId,
          finalDisciplinaryAction,
        };
        break;
      case EntityType.PRDNDRRecord:
        component = PilotNdrRecordEditDialogComponent;
        const ndrRecord = await this._ndrRecord
          .getById(entityId)
          .pipe(map((col) => col.results[0]))
          .toPromise();
        data = {
          pilotId,
          ndrRecord,
        };
        break;
      case EntityType.PRDDrugAndAlcoholRecord:
        component = PilotDrugAlcoholRecordEditDialogComponent;
        const drugAlcoholRecord = await this._drugAlcoholRecord
          .getById(entityId)
          .pipe(map((col) => col.results[0]))
          .toPromise();
        data = {
          pilotId,
          drugAlcoholRecord,
        };
        break;
    }
    if (component !== undefined) {
      const dialogRef = this._dialogService.openDraggable<typeof component.prototype>({
        component,
        config: {
          data,
          disableClose: true,
        },
        dragHandleSelectors: ['.drag-handle'],
        draggableClass: 'draggable-dialog',
      });
      dialogRef.matDialogRef
        .afterClosed()
        .pipe(take(1))
        .subscribe(async () => {
          this.markAsLoading();
          await sleep(2000);
          this._pageRefresh$.next({});
        });
    }
  }

  public patchQueryParams<T extends {}>(params: T | undefined) {
    params = params != null ? params : ({} as any);
    const airCarrierAbbreviation = this._route.snapshot.data.airCarrier.abbreviation;
    const currentQueryparams = this._route.snapshot.queryParams;
    const queryParams = {
      ...currentQueryparams,
      ...params,
    };
    this._router.navigate(
      [`/prd/${airCarrierAbbreviation}/transmission-runs`, this.transmissionRunId],
      { queryParams },
    );
  }

  get showErrorsOnly() {
    return this.transmissionRunBatchItemSummaryParameters.get('showErrorsOnly');
  }

  private hasError(showErrorsOnly?: boolean) {
    return showErrorsOnly === true ? true : undefined;
  }

  public humanizeEntityName(entityName: string) {
    return humanizeEntityName(entityName);
  }

  public refresh() {
    this._pageRefresh$.next({});
  }
}
